import { cloneDeep } from "lodash";
import { BOTTOM_BUTTONS, Dart, DART_HITS, DART_NUMBER, X01_ASP } from "../types/dart";
import { X01GameState, X01ASPs, X01Results, requiredDoubleOut, X01TeamState, X01PlayerState, X01ASPWithThrow } from "../types/x01";
import { BOT_DIFFICULTY, MATCH_TYPE } from "../types/match";
import { PLAYER } from "../types/players";
import { dartAndScores, InOut, potentialOuts } from "../types/x01";
import { botHitChance, getNextThrower } from "./matchLogic";
import { playAudio } from "./sounds";
import { GAME_STATE } from "../types/game";
import { PlayerTeamIdx, TEAM } from "../types/teams";
import { getOpponentsTeam, getPlayersTeam } from "./teamLogic";
import { getInOutLocalStorage, getX01GameLocalStorage } from "./localStorage";

export const initX01GameState: X01GameState = {
  gameState: GAME_STATE.NOT_STARTED,
  throwNumber: 1,
  currentPlayer: PLAYER.FIRST,
  x01Game: getX01GameLocalStorage(),
  inOut: getInOutLocalStorage(),
  [TEAM.ONE]: {
    score: getX01GameLocalStorage(),
    roundScore: 0,
    previousRounds: [],
    doubledIn: false,
    players: [
      {
        name: PLAYER.FIRST,
        totalThrows: 0,
        inShots: 0,
        outShots: 0,
        scored: 0,
        gotIn: false,
        gotOut: false,
        asps: {
          [X01_ASP.TWENTY_SIX]: 0,
          [X01_ASP.TON]: 0,
          [X01_ASP.TON_TWENTY]: 0,
          [X01_ASP.TON_FORTY]: 0,
          [X01_ASP.TON_SIXTY]: 0,
          [X01_ASP.TON_EIGHTY]: 0
        }
      }
    ]
  },
  [TEAM.TWO]: {
    score: getX01GameLocalStorage(),
    roundScore: 0,
    previousRounds: [],
    doubledIn: false,
    players: [
      {
        name: PLAYER.SECOND,
        totalThrows: 0,
        inShots: 0,
        outShots: 0,
        scored: 0,
        gotIn: false,
        gotOut: false,
        asps: {
          [X01_ASP.TWENTY_SIX]: 0,
          [X01_ASP.TON]: 0,
          [X01_ASP.TON_TWENTY]: 0,
          [X01_ASP.TON_FORTY]: 0,
          [X01_ASP.TON_SIXTY]: 0,
          [X01_ASP.TON_EIGHTY]: 0
        }
      }
    ]
  },
};

export const initResults: X01Results = {
  [TEAM.ONE]: [],
  [TEAM.TWO]: []
}

export const initPlayerState: X01PlayerState = {
  name: '',
  totalThrows: 0,
  inShots: 0,
  outShots: 0,
  scored: 0,
  gotIn: false,
  gotOut: false,
  asps: {
    [X01_ASP.TWENTY_SIX]: 0,
    [X01_ASP.TON]: 0,
    [X01_ASP.TON_TWENTY]: 0,
    [X01_ASP.TON_FORTY]: 0,
    [X01_ASP.TON_SIXTY]: 0,
    [X01_ASP.TON_EIGHTY]: 0
  }
}

export const initPlayerResultASPs: X01ASPWithThrow = {
  throws: 0,
  scored: 0,
  [X01_ASP.TWENTY_SIX]: 0,
  [X01_ASP.TON]: 0,
  [X01_ASP.TON_TWENTY]: 0,
  [X01_ASP.TON_FORTY]: 0,
  [X01_ASP.TON_SIXTY]: 0,
  [X01_ASP.TON_EIGHTY]: 0,
}

export function getX01InitGameState(x01Game: number, inOut: InOut, matchType: MATCH_TYPE): X01GameState {
  const newGameState = cloneDeep(initX01GameState);
  newGameState.x01Game = x01Game;
  newGameState.team1.score = x01Game;
  newGameState.team2.score = x01Game;
  newGameState.inOut = inOut;
  if (matchType === MATCH_TYPE.DOUBLES) {
    newGameState.team1.players.push({ ...cloneDeep(initPlayerState), name: PLAYER.THIRD });
    newGameState.team2.players.push({ ...cloneDeep(initPlayerState), name: PLAYER.FOURTH });
  }
  if (inOut !== InOut.DOUBLE_IN_DOUBLE_OUT) {
    newGameState.team1.doubledIn = true;
    newGameState.team2.doubledIn = true;
  }
  return newGameState;
}


function getOpponent(gameState: X01GameState): { opponent: PLAYER, opponentState: X01TeamState } {
  const { currentPlayer } = gameState;
  const opponent: PLAYER = getNextThrower(gameState);
  const opponentTeam: TEAM = getOpponentsTeam(currentPlayer);

  return {
    opponent,
    opponentState: gameState[opponentTeam]
  };
}

function getASPs(gameState: X01GameState): X01ASPs {
  const team = getPlayersTeam(gameState.currentPlayer);
  const { currentPlayer } = gameState;
  let currentPlayerGameState = gameState[team].players[PlayerTeamIdx[currentPlayer]];
  const asps = { ...currentPlayerGameState.asps } as X01ASPs;
  const { roundScore } = gameState[team];
  if (roundScore === X01_ASP.TWENTY_SIX) {
    asps[X01_ASP.TWENTY_SIX] += 1;
  } else if (roundScore === X01_ASP.TON_EIGHTY) {
    asps[X01_ASP.TON_EIGHTY] += 1;
  } else if (roundScore >= X01_ASP.TON_SIXTY) {
    asps[X01_ASP.TON_SIXTY] += 1;
  } else if (roundScore >= X01_ASP.TON_FORTY) {
    asps[X01_ASP.TON_FORTY] += 1;
  } else if (roundScore >= X01_ASP.TON_TWENTY) {
    asps[X01_ASP.TON_TWENTY] += 1;
  } else if (roundScore >= X01_ASP.TON) {
    asps[X01_ASP.TON] += 1;
  }

  return asps;
}

function setASPs(gameState: X01GameState): X01GameState {
  const newState = { ...gameState };
  const asps = getASPs(gameState);
  const team = getPlayersTeam(gameState.currentPlayer);

  newState[team].players[PlayerTeamIdx[gameState.currentPlayer]].asps = asps;
  return newState;
}

function changeTurns(gameState: X01GameState): X01GameState {
  const newState = { ...gameState };
  const { opponent } = getOpponent(newState);
  const team = getPlayersTeam(gameState.currentPlayer);
  const { score, roundScore } = newState[team];
  setASPs(newState);
  newState[team].previousRounds.push({
    roundScore,
    startingScore: score + roundScore
  })
  newState[team].players[PlayerTeamIdx[gameState.currentPlayer]].scored += newState[team].roundScore;
  newState[team].roundScore = 0;
  newState.currentPlayer = opponent;
  return newState;
}

function bust(gameState: X01GameState): X01GameState {
  const newState = { ...gameState };
  const team = getPlayersTeam(gameState.currentPlayer);
  newState[team].score = newState[team].score + newState[team].roundScore;
  newState[team].roundScore = 0;
  newState.throwNumber = 3;
  return newState;
}

function setX01Score({ number, multiplier }: Dart, gameState: X01GameState): X01GameState {
  const newState = cloneDeep(gameState);
  const { currentPlayer } = newState;
  const team = getPlayersTeam(gameState.currentPlayer);
  const { score } = newState[team];
  const { totalThrows } = newState[team].players[PlayerTeamIdx[currentPlayer]];
  // Adds one to the throw count
  newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows = totalThrows + 1;
  if (gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT && !gameState[team].doubledIn) {
    newState[team].players[PlayerTeamIdx[currentPlayer]].inShots += 1;
    if (multiplier !== DART_HITS.DOUBLE) {
      playAudio(BOTTOM_BUTTONS.MISS);
      return newState;
    } else {
      newState[team].players[PlayerTeamIdx[currentPlayer]].gotIn = true;
      newState[team].doubledIn = true;
    }
  };
  if ((gameState.inOut === InOut.ANY_IN_DOUBLE_OUT || gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT) && potentialOuts.includes(score)) {
    newState[team].players[PlayerTeamIdx[currentPlayer]].outShots += 1;
  }

  const pointsHit = number * multiplier;
  const newScore = score - pointsHit;
  if (
    newScore < 0
    || (newScore === 1 && requiredDoubleOut.includes(newState.inOut))
    || (newScore === 0 && multiplier !== DART_HITS.DOUBLE && requiredDoubleOut.includes(newState.inOut))
  ) {
    playAudio(BOTTOM_BUTTONS.END_TURN);
    return bust(newState);
  }
  newState[team].roundScore += pointsHit;
  newState[team].score = newScore;
  playAudio(multiplier);
  if (newScore !== 0) return newState;
  if (!requiredDoubleOut.includes(newState.inOut) || multiplier === DART_HITS.DOUBLE) {
    setASPs(newState);
    newState.gameState = GAME_STATE.PENDING;
    newState[team].players[PlayerTeamIdx[currentPlayer]].gotOut = true;
  }
  return newState;
}

function getEndOrMissedState(number: BOTTOM_BUTTONS, gameState: X01GameState): X01GameState {
  const newState = { ...gameState };
  const { currentPlayer } = newState;
  const team = getPlayersTeam(gameState.currentPlayer);
  const { doubledIn, score } = newState[team];
  playAudio(number);
  if (number === BOTTOM_BUTTONS.MISS) {
    newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += 1;
    newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
    if (!doubledIn && gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT) {
      newState[team].players[PlayerTeamIdx[currentPlayer]].inShots += 1;
    } else if (potentialOuts.includes(score) && (gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT || gameState.inOut === InOut.ANY_IN_DOUBLE_OUT)) {
      newState[team].players[PlayerTeamIdx[currentPlayer]].outShots += 1;
    }
    if (newState.throwNumber === 1) return changeTurns(newState);
  } else if (number === BOTTOM_BUTTONS.END_TURN) {
    const missedThrows = 4 - newState.throwNumber;
    newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += missedThrows;
    newState.throwNumber = 1;
    if (!doubledIn && gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT) {
      newState[team].players[PlayerTeamIdx[currentPlayer]].inShots += missedThrows;
    } else if (potentialOuts.includes(score) && (gameState.inOut === InOut.DOUBLE_IN_DOUBLE_OUT || gameState.inOut === InOut.ANY_IN_DOUBLE_OUT)) {
      newState[team].players[PlayerTeamIdx[currentPlayer]].outShots += missedThrows;
    }
    return changeTurns(newState);
  }
  return newState;
}

export function getNextX01State(dart: Dart, gameState: X01GameState): X01GameState {
  let newState = cloneDeep(gameState);
  const team = getPlayersTeam(gameState.currentPlayer);
  const { number } = dart;
  if (number === BOTTOM_BUTTONS.MISS || number === BOTTOM_BUTTONS.END_TURN) {
    return getEndOrMissedState(number, newState);
  }
  newState = setX01Score(dart, newState);
  if (newState.gameState === GAME_STATE.PENDING) {
    newState[team].players[PlayerTeamIdx[gameState.currentPlayer]].scored += newState[team].roundScore;
    return newState;
  }
  newState.throwNumber = newState.throwNumber === 3 ? 1 : newState.throwNumber + 1;
  if (newState.throwNumber === 1) {
    return changeTurns(newState);
  }
  return newState;
};

export function getNextX01NumpadState(roundScore: number, gameState: X01GameState): X01GameState {
  let newState = cloneDeep(gameState);
  let { currentPlayer } = gameState;
  const team = getPlayersTeam(currentPlayer);

  const newScore = newState[team].score - roundScore;
  if (newScore < 0 || newScore === 1) {
    newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += 1;
    return changeTurns(newState)
  }
  newState[team].score = newState[team].score - roundScore;
  newState[team].roundScore = roundScore;
  newState[team].players[PlayerTeamIdx[currentPlayer]].totalThrows += 3;
  if (newScore === 0) {
    newState.gameState = GAME_STATE.PENDING;
    newState[team].players[PlayerTeamIdx[gameState.currentPlayer]].scored += roundScore;
    return newState;
  }
  return changeTurns(newState)
};

export function endGame(gameState: X01GameState): X01GameState {
  const newState = cloneDeep(gameState);
  newState.gameState = GAME_STATE.FINISHED;
  newState.winner = getPlayersTeam(newState.currentPlayer);
  return newState;
}

export function doBotMove(gameState: X01GameState, botDifficulty: BOT_DIFFICULTY): X01GameState {
  const newState = cloneDeep(gameState);
  const botTurnScore = getBotTurnScore(botDifficulty);
  const nextDart = getNextBotDart(botTurnScore, newState.team2, botDifficulty);

  return getNextX01State(nextDart, newState);
}

export function doNumpadBotMove(gameState: X01GameState, botDifficulty: BOT_DIFFICULTY): X01GameState {
  const newState = cloneDeep(gameState);
  const botTurnScore = getBotNumpadRoundScore(newState, botDifficulty);

  return getNextX01NumpadState(botTurnScore, newState);
}

function getBotNumpadRoundScore(gameState: X01GameState, botDifficulty: BOT_DIFFICULTY): number {
  const scoreLeft = gameState.team2.score;
  let roundScore = 0;
  if (potentialOuts.includes(scoreLeft)) {
    roundScore = getBotDouble(botDifficulty) === DART_HITS.DOUBLE ? scoreLeft : Math.round(scoreLeft / 2);
    return roundScore;
  }

  for (let i = 0; i < 3; i++) {
    const dartScore = getBotTurnScore(botDifficulty);
    const currentScoreLeft = scoreLeft - roundScore;
    if (potentialOuts.includes(currentScoreLeft)) {
      if (getBotDouble(botDifficulty) === DART_HITS.DOUBLE) {
        roundScore = scoreLeft;
        break;
      }
      roundScore = Math.round(currentScoreLeft / 2);
      continue;
    }
    if (currentScoreLeft < 40) {
      roundScore += 1;
      continue;
    }
    const nextScoreLeft = scoreLeft - (roundScore + dartScore);
    if (nextScoreLeft < 32) {
      roundScore = scoreLeft - 32;
      continue;
    } else if (nextScoreLeft < 40) {
      roundScore = scoreLeft - 40;
      continue;
    }
    roundScore += dartScore;
  }
  return roundScore;
}

export function getBotTurnScore(botDifficulty: BOT_DIFFICULTY): number {
  let turnScore = 0;
  switch (botDifficulty) {
    case BOT_DIFFICULTY.EASY:
      turnScore = Math.floor(Math.random() * Math.floor(11 - 7)) + 7;
      break;
    case BOT_DIFFICULTY.MEDIUM:
      turnScore = Math.floor(Math.random() * Math.floor(17 - 14)) + 14;
      break;
    case BOT_DIFFICULTY.HARD:
      turnScore = Math.floor(Math.random() * Math.floor(21 - 19)) + 19;
      break;
    // case BOT_DIFFICULTY.PRO:
    //   turnScore = Math.floor(Math.random() * Math.floor(10 - 5)) + 5;
    //   break;
  }
  return turnScore;
}

function getNextBotDart(botTurnScore: number, { doubledIn, score }: X01TeamState, botDifficulty: BOT_DIFFICULTY): Dart {
  const nextDart: Dart = findAppropriateDart(botTurnScore);

  if (!doubledIn) {
    nextDart.number = findAppropriateDart(Math.floor(botTurnScore / 2)).number;
    nextDart.multiplier = getBotDouble(botDifficulty);
  } else if (potentialOuts.includes(score)) {
    nextDart.number = findAppropriateDart(Math.floor(score / 2)).number;
    nextDart.multiplier = getBotDouble(botDifficulty);
  } else if (score <= 40) {
    nextDart.number = findAppropriateDart(1).number;
  }

  return nextDart;
}

function findAppropriateDart(score: number): Dart {
  const nextDart: Dart = { number: DART_NUMBER.ONE, multiplier: DART_HITS.SINGLE };
  let found = false;
  const loop = (score: number) => {
    for (const { dart, scores } of dartAndScores) {
      const scoreIdx = scores.indexOf(score);
      if (scoreIdx < 0) continue;
      nextDart.multiplier = getDartHit(scoreIdx);
      nextDart.number = dart;
      found = true;
      break;
    };
  }
  for (let i = 0; i < 3; i++) {
    if (!found) loop(score - i);
  }
  return nextDart
}

function getBotDouble(botDifficulty: BOT_DIFFICULTY): DART_HITS {
  const botHit = botHitChance(botDifficulty);
  return botHit ? DART_HITS.DOUBLE : DART_HITS.SINGLE;
}

function getDartHit(idx: number): DART_HITS {
  if (idx === 0) return DART_HITS.SINGLE;
  if (idx === 1) return DART_HITS.DOUBLE;
  if (idx === 2) return DART_HITS.TRIPLE;
  return DART_HITS.SINGLE;
}